import { Button, Dialog, DialogContent, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useState, useEffect } from 'react';
import { CheckBoxField } from '../../../../lib/CheckBoxField';

interface OptOutModalProps {
    showModal: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Opt-Out of Nomination Confirmation Modal
 * Shows detailed implications and requires checkbox confirmation
 */
function OptOutModal({ showModal, onConfirm, onCancel }: OptOutModalProps) {
    const [isConfirmed, setIsConfirmed] = useState(false);

    // Reset checkbox when modal opens
    useEffect(() => {
        if (showModal) {
            setIsConfirmed(false);
        }
    }, [showModal]);

    const handleConfirm = () => {
        if (isConfirmed) {
            onConfirm();
        }
    };

    return (
        <Dialog
            maxWidth="sm"
            fullWidth
            open={showModal}
            onClose={onCancel}
            aria-labelledby="opt-out-modal"
            className="opt-out-modal-dialog"
        >
            <div className="opt-out-modal-header">
                <span className="opt-out-modal-title">Confirm Opt-Out of Nomination</span>
                <IconButton aria-label="close" onClick={onCancel} className="close-btn">
                    <CloseIcon />
                </IconButton>
            </div>
            <DialogContent className="opt-out-modal-content">
                <div className="opt-out-modal-body">
                    <p className="intro-text">
                        I hereby confirm that I do not wish to appoint any nominee(s) to my demat
                        account / mutual fund folio at this point of time.
                    </p>
                    <p className="understand-text">I understand that –</p>
                    <ul className="implications-list">
                        <li>
                            the nomination helps to quickly identify the person for transfer of
                            securities and helps in faster and smoother transmission of my
                            securities to my legal heir(s) after my demise.
                        </li>
                        <li>
                            in the absence of a nomination, my legal heir(s) may require the
                            submission of certain additional legal or court-issued documents which
                            may delay the process of transmission of securities to my legal
                            heir(s).
                        </li>
                        <li>
                            if no claim is made on the account / folio for a prolonged period after
                            my demise, the holdings may be treated as unclaimed assets, and they
                            may be transferred to Investor Education and Protection Fund Authority
                            (IEPF) in accordance with the applicable regulatory framework.
                        </li>
                    </ul>

                    <div className="confirmation-checkbox">
                        <CheckBoxField
                            label="I confirm that I have understood the above implications and that my decision to opt out of nomination is voluntary."
                            value={isConfirmed}
                            onChange={(e: any) => setIsConfirmed(e.target.checked)}
                        />
                    </div>

                    <div className="modal-buttons">
                        <Button
                            variant="outlined"
                            className="go-back-btn"
                            onClick={onCancel}
                        >
                            Go Back
                        </Button>
                        <Button
                            variant="contained"
                            className={`confirm-btn ${isConfirmed ? 'enabled' : 'disabled'}`}
                            onClick={handleConfirm}
                            disabled={!isConfirmed}
                        >
                            Confirm Opt-Out
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default OptOutModal;
